import React, { useMemo, useState } from 'react'
import {
  BsCollection,
  BsExclamationTriangle,
  BsFileText,
  BsFiles,
  BsGrid3X3Gap,
  BsHash,
} from 'react-icons/bs'
import { ChartRecommendationPanel, AIAssistantPanel } from '../MvpAssistant/Panels'
import styles from './InsightsTray.module.scss'

function getSchemaCount(schema, predicate) {
  return Object.values(schema || {}).filter(predicate).length
}

function InsightCard({ icon: Icon, label, value }) {
  return (
    <div className={styles.card}>
      <span className={styles.iconWrap}>
        <Icon />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  )
}

function InsightsTray({
  auditSummary,
  largeDatasetInfo,
  recommendations,
  charts,
  onSelectRecommendation,
  aiResult,
  onApplyFixes,
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  const counts = useMemo(() => {
    if (!auditSummary) return null

    const schema = auditSummary.schema || {}
    const missingValues = Object.values(schema).reduce(
      (sum, column) => sum + (column.missingCount || 0),
      0
    )

    return {
      missingValues,
      duplicateRows: auditSummary.duplicateRows || 0,
      possibleIds: getSchemaCount(schema, (column) => column.likelyId),
      unusualValues: getSchemaCount(
        schema,
        (column) => (column.outlierCount || 0) > 0
      ),
    }
  }, [auditSummary])

  if (!auditSummary || !counts) return null

  function handleApplyFixes() {
    if (
      !window.confirm(
        'Apply safe fixes? This will trim whitespace, normalize numbers and dates, and remove empty rows.'
      )
    ) {
      return
    }

    onApplyFixes()
  }

  return (
    <div className={styles.tray}>
      <div className={styles.header}>
        <div>
          <h4 className="mb-1">Data Insights</h4>
          <p className="mb-0 text-muted">
            Quick checks on structure, blanks, duplicates, and chart fit.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? 'Hide details' : 'Review details'}
        </button>
      </div>

      <div className={styles.grid}>
        <InsightCard icon={BsCollection} label="Rows" value={auditSummary.rows} />
        <InsightCard icon={BsGrid3X3Gap} label="Columns" value={auditSummary.columns} />
        <InsightCard icon={BsFileText} label="Blank cells" value={counts.missingValues} />
        <InsightCard icon={BsFiles} label="Duplicate rows" value={counts.duplicateRows} />
        <InsightCard icon={BsHash} label="Possible ID fields" value={counts.possibleIds} />
        <InsightCard icon={BsExclamationTriangle} label="Columns with unusual values" value={counts.unusualValues} />
      </div>

      {isExpanded && (
        <div className={styles.details}>
          {auditSummary.sampled && (
            <div className="alert alert-secondary py-2 mb-3">
              <strong>Large dataset mode</strong>
              <div className="small mt-1">
                {largeDatasetInfo?.isLarge ? 'Large dataset detected. ' : ''}
                Audit based on the first {auditSummary.rowsAnalyzed.toLocaleString()} of{' '}
                {auditSummary.rows.toLocaleString()} rows to keep the app responsive.
              </div>
            </div>
          )}

          {auditSummary.warnings?.length > 0 && (
            <div className="alert alert-warning py-2 mb-3">
              <strong>Things to review</strong>
              <ul className="mb-0 mt-1">
                {auditSummary.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className={styles.actionRow}>
            <button className="btn btn-sm btn-primary" onClick={handleApplyFixes}>
              Apply safe fixes
            </button>
            {auditSummary.recommendedFixes?.length > 0 && (
              <span className="small text-muted">
                Suggested cleanup: {auditSummary.recommendedFixes.join(' · ')}
              </span>
            )}
          </div>

          <div className={styles.schemaList}>
            {Object.entries(auditSummary.schema || {}).map(([name, info]) => (
              <div key={name} className={styles.schemaItem}>
                <strong>{name}</strong>
                <span>
                  {info.type}
                  {info.missingCount ? ` · ${info.missingCount} blanks` : ' · no blanks'}
                  {info.likelyId ? ' · likely ID' : ''}
                  {info.likelyMeasure ? ' · numeric measure' : ''}
                  {info.tooManyUniques ? ' · many unique values' : ''}
                </span>
              </div>
            ))}
          </div>

          <div className={styles.recommendationsBlock}>
            <div className={styles.blockTitle}>
              <BsCollection />
              <span>Suggested next charts</span>
            </div>
            <ChartRecommendationPanel
              recommendations={recommendations}
              charts={charts}
              onSelectChart={onSelectRecommendation}
            />
          </div>

          <div className={styles.recommendationsBlock}>
            <AIAssistantPanel aiResult={aiResult} />
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(InsightsTray)
